import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { memo, type ReactNode, useCallback, useId, useRef } from 'react';

import {
  PLAYER_PREVIEW_NAME,
  PlayerPreviewProvider,
  type PlayerPreviewScopeProps,
} from './PlayerPreviewContextProvider';

export type PlayerPreviewProps = {
  children: ReactNode;
  defaultOpen?: boolean;
  defaultPosition?: number;
  onOpenChange?: (open: boolean) => void;
  onPositionChange?: (position: number) => void;
  open?: boolean;
  position?: number;
};

export const PlayerPreview = memo(
  ({
    __scopePlayerPreview,
    children,
    defaultOpen,
    defaultPosition,
    onOpenChange,
    onPositionChange,
    open: openProp,
    position: positionProp,
  }: PlayerPreviewScopeProps<PlayerPreviewProps>) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useControllableState({
      caller: PLAYER_PREVIEW_NAME,
      defaultProp: defaultOpen ?? false,
      onChange: onOpenChange,
      prop: openProp,
    });
    const [position, setPosition] = useControllableState({
      caller: PLAYER_PREVIEW_NAME,
      defaultProp: defaultPosition ?? 0,
      onChange: onPositionChange,
      prop: positionProp,
    });

    const handlePreviewStart = useCallback(
      (position: number) => {
        setPosition(position);
        setOpen(true);
      },
      [setPosition, setOpen],
    );

    const handlePreviewEnd = useCallback(() => {
      setOpen(false);
      setPosition(0);
    }, [setPosition, setOpen]);

    return (
      <PlayerPreviewProvider
        contentId={useId()}
        contentRef={contentRef}
        onPreviewEnd={handlePreviewEnd}
        onPreviewStart={handlePreviewStart}
        open={open}
        position={position}
        scope={__scopePlayerPreview}
        triggerRef={triggerRef}
      >
        {children}
      </PlayerPreviewProvider>
    );
  },
);

PlayerPreview.displayName = 'PlayerPreview';
